import React, { useState, useRef,useEffect, Dispatch,SetStateAction ,useCallback } from 'react';
import { X, Globe, MoreVertical, Plus,CirclePlus , Check, ChevronUp, ChevronDown, Trash2, Loader2, CheckCircle2,Bot ,Award, DiscIcon,CircleDot ,Download ,Info ,Loader,CheckCircle,AlertCircle  } from 'lucide-react';
import "../styles/loader.css";
import { useDropzone, FileWithPath, DropzoneOptions } from 'react-dropzone';
import { uploadCustomFile,validateFileHeaders } from '../services/api/virtualAssistant'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileWord, faFilePdf, faFileAlt, faFilePowerpoint, faFileImage, faFileExcel, faFileVideo, faFile } from '@fortawesome/free-solid-svg-icons';

interface CalenderFile {
  file: File;
  id: string;
  status: 'uploading' | 'completed' | 'error';
  progress: number;
  file_name: string;
  size: number;
  error: string | null;
  location: string | null;
  sequence: string | '1';
  file_type: string | null;
  file_extn: string | null;
  file_url?: string | null; 
}

interface CalenderScheduleProps {
  isCalenderModalOpen: boolean;
  setCalenderModalOpen: (isOpen: boolean) => void;
  assistant: any;
  onUploadComplete: (files: CalenderFile[]) => void;
  triggerAutoSave: () => void; 
}

interface ConnectionResponse {
  message: string;
  data: any;
  connection_status: 'success' | 'failure';
}
const CalenderSchedule: React.FC<CalenderScheduleProps> = ({
  isCalenderModalOpen,
  setCalenderModalOpen,
  assistant , onUploadComplete, triggerAutoSave
}) => {
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);
  const [validationResult, setValidationResult] = useState(null);
  const [activeTab, setActiveTabs] = useState<'DOWNLOAD TEMPLATE' | 'UPLOAD FILE'>('DOWNLOAD TEMPLATE');
   // Update the handleToggleChange function to accept the correct type
   const handleTabChange = (tab: 'DOWNLOAD TEMPLATE' | 'UPLOAD FILE') => {
    setActiveTabs(tab);
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  const getMaxFileSizeDisplay = () => {
    const maxSize = getMaxFileSize();
    return formatFileSize(maxSize);
  };
  const getFileCategory = (extension: string): string => {
    const imageExtensions = ["png", "jpg", "jpeg", "bmp", "gif", "tiff", "heif"];
    const pdfExtensions = ["pdf"];
    const documentExtensions = ["doc", "docx", "txt", "rtf"];
    const spreadsheetExtensions = ["xls", "xlsx", "csv"];
    const presentationExtensions = ["ppt", "pptx"];
    const videoExtensions = ["mp4", "mov", "avi", "mkv", "flv", "wmv"];
  
    if (imageExtensions.includes(extension)) return "image";
    if (pdfExtensions.includes(extension)) return "pdf";
    if (documentExtensions.includes(extension)) return "doc";
    if (spreadsheetExtensions.includes(extension)) return "spreadsheet";
    if (presentationExtensions.includes(extension)) return "presentation";
    if (videoExtensions.includes(extension)) return "video";
  
    return "other"; // Default fallback
  };
  const getFileIcon = (fileName: string) => {
      const extension = fileName.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'xlsx':
        case 'xls':
          return faFileExcel;
        default:
          return faFile;
      }
    };
  
  const getAllowedFileFormats = () => {
    const formats = systemConfig.find(item => item.Id === 'allowed_file_formats')?.ConfigValue || '';
    const formatArray = formats.split(',').map(format => format.trim().toLowerCase());
    
    // Create accept object for dropzone
    const acceptObject: { [key: string]: string[] } = {};
    formatArray.forEach(format => {
      // Map file extensions to MIME types
      if (format.startsWith('.')) {
        const ext = format;
        // Add common MIME types for supported formats
        switch (ext.toLowerCase()) {
          case '.xlsx':
          case '.xls':
            acceptObject['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = [ext];
            break;
          default:
            // For unknown types, use a generic binary type
            acceptObject['application/octet-stream'] = [ext];
        }
      }
    });
    return acceptObject;
  };
  const getMaxFileSize = () => {
    const maxSize = systemConfig.find(item => item.Id === 'max_allowed_calendar_file_size')?.ConfigValue || '1';
    return maxSize ? parseInt(maxSize) * 1024 * 1024 : 1 * 1024 * 1024; // Default to 500MB if not specified
  };
  const [uploadedCalenderFiles, setUploadedCalenderFiles] = useState<CalenderFile[]>(assistant?.calendar_data || []);
    const onDrop = useCallback<NonNullable<DropzoneOptions['onDrop']>>(async (acceptedFiles, rejectedFiles) => {
      if (uploadedCalenderFiles.length >= 1) {
        return;
      }
    
        // Handle rejected files
        if (rejectedFiles.length > 0) {
          // console.log('Some files were rejected:', rejectedFiles);
          return;
        }
      
        const maxSequence = uploadedCalenderFiles.length > 0 ? Math.max(...uploadedCalenderFiles.map(file => parseInt(file.sequence))) : 0;
      
        const newFiles: CalenderFile[] = acceptedFiles.map((file, index) => {
          const extension = file.name.split(".").pop()?.toLowerCase() || ""; // Get file extension
          return {
            file,
            id: Math.random().toString(36).substr(2, 9),
            status: "uploading",
            progress: 0,
            file_name: file.name,
            size: file.size,
            error: null,
            location: null,
            sequence: (maxSequence + index + 1).toString(),
            file_url: "",
            file_type: getFileCategory(extension), // Categorized file type (image, pdf, doc, etc.)
            file_extn: extension ? `.${extension}` : "", // File extension with dot prefix
          };
        });
      
        setUploadedCalenderFiles(prev => [...prev, ...newFiles]);
      
        // Upload each file
        newFiles.forEach(async (fileObj) => {
          try {
            const validationData = await validateFileHeaders(fileObj.file);
            let status = validationData?.status ;
            let msg = validationData?.message ;
            if(status === 'success'){
                  const { fileName, fileLocation } = await uploadCustomFile(fileObj.file, 'general', assistant.app_id, localStorage.getItem('netId') || '');
                  // console.log('File uploaded to:', fileLocation); // Debugging statement
                  setUploadedCalenderFiles(prev => {
                    const updatedFiles = prev.map(f => 
                      f.id === fileObj.id 
                        ? { ...f, status: 'completed' as 'completed', location:fileLocation, progress: 100, file_url: fileLocation } 
                        : f
                    );
                    // console.log('Updated uploadedFiles:', updatedFiles); // Debugging statement
                    // onUploadComplete(updatedFiles); 
                    return updatedFiles;
                  });
                  triggerAutoSave();  // Trigger auto-save after file upload completes
              }
              else if(status === 'error'){
                setUploadedCalenderFiles(prev => prev.map(f => 
                  f.id === fileObj.id 
                    ? { ...f, status: 'error', error: msg, progress: 0 } 
                    : f
                ));
              }
            } catch (error) {
                  setUploadedCalenderFiles(prev => prev.map(f => 
                    f.id === fileObj.id 
                      ? { ...f, status: 'error', error: error instanceof Error ? error.message : 'Upload failed', progress: 0 } 
                      : f
                  ));
            }
              });
      }, [uploadedCalenderFiles, triggerAutoSave]);
      const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        multiple: false,
        accept: getAllowedFileFormats(),
        maxSize: getMaxFileSize(),
      });

useEffect(() => {
  const config = getCachedData('systemConfig');
  if (config) {
    setSystemConfig(config);
  }
}, []);
  const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000
  const max_rows = systemConfig.find(item => item.Id === 'max_allowed_calendar_rows')?.ConfigValue || 50;

  const getCachedData = (key: string) => {
    const item = sessionStorage.getItem(key);
    if (!item) return null;

    const { value, timestamp } = JSON.parse(item);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  };
 
 useEffect(() => {
     if (assistant?.app_id && uploadedCalenderFiles.length === 0) {
       const initialFiles: CalenderFile[] = assistant?.calendar_data || [];
   
       // Process files: Set status to "completed" and ensure size is defined
       const updatedFiles: CalenderFile[] = initialFiles.map((file: CalenderFile) => ({
         ...file,
         status: "completed" as "completed",
         size: file.size ?? 0, // Ensure size is a number
       }));
   
       // Update state only once
       setUploadedCalenderFiles(updatedFiles);
     }
   }, [assistant]);

   const removeFile = (fileId: string): void => {
    setUploadedCalenderFiles(prev => prev.filter(f => f.sequence !== fileId));
    triggerAutoSave();
  };   
   
 
   useEffect(() => {
     
      //  console.log("📤 Notifying Parent with updated uploadedFiles:", uploadedCalenderFiles);
       onUploadComplete([...uploadedCalenderFiles]); // Pass the latest files
   }, [uploadedCalenderFiles]);
  
 
   const handleDownloadClick = () =>{
    // console.log("handle download")
    const link = document.createElement('a');
    link.href = `/course_schedule.xlsx`; // correct path
    link.download = 'course_schedule.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
   }
  const setActiveTab = (tab: 'DOWNLOAD TEMPLATE' | 'UPLOAD FILE') => {
    
  };
  
  return  (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60 ${
        isCalenderModalOpen ? 'block' : 'hidden'
      }`}
      onClick={() => {
        setCalenderModalOpen(false);
      }}
    >
      <div
        className="bg-white w-[50%] h-[50%] shadow-lg rounded-lg flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faFileExcel} className="h-6 w-6 text-gray-700" />
              <h2 className="text-xl font-semibold text-gray-800">Calendar Schedule</h2>
            </div>
            <button
              onClick={() => setCalenderModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-600 mt-2 ml-8 flex justify-between items-center">
          Use this section to upload any calendar schedule. You can upload only 1 file.
            <button
              className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-500 rounded-full text-black font-semibold hover:bg-black hover:text-white ml-auto"
              onClick={() => handleDownloadClick()}
            >
             <Download className="w-5 h-5 " /> Download template
            </button>
          </p>

          <hr className="mt-4" />
        </div>
  
        {/* Main Content */}
        <div className="p-2 flex flex-col">
          {/* File Upload Section */}
          <div className="space-y-6 items-center justify-center space-y-6">
            <div className="flex border rounded-lg p-4">
              <div className="w-[100%] p-2 max-h-40 sticky top-0 overflow-y-auto">
              {uploadedCalenderFiles.length === 0 && (
                <div
                  {...getRootProps()}
                  className={`dropzone w-full h-36 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-colors
                    ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
                    ${fileRejections.length > 0 ? 'border-red-500' : ''}`}
                >
                  <input {...getInputProps()} />
                  <div className="text-center p-4">
                    {isDragActive ? (
                      <p className="text-blue-600">Drop the files here</p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-gray-600">Drag and drop files here, or click to select files</p>
                        <div className="flex items-center justify-center space-x-2">
                          <p className="text-sm text-gray-500">
                            Supports xlsx, xls formats (max {getMaxFileSizeDisplay()}, max {max_rows} rows in excel)
                          </p>
                          <div className="relative group">
                            <Info className="h-4 w-4 text-gray-500 cursor-pointer" />
                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-64">
                              <p><strong>Spreadsheets:</strong> XLSX, XLS</p>
                            </div>
                          </div>
                        </div>
                        {fileRejections.length > 0 && (
                          <p className="text-sm text-red-500">
                            Some files were rejected. Please check the file type and size limits.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
                {uploadedCalenderFiles.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 mt-2">
                    <div className="p-4">
                      <h3 className="text-lg font-semibold mb-4">Uploaded File</h3>
                      <div className="space-y-3">
                        {uploadedCalenderFiles.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3 flex-1">
                              <FontAwesomeIcon icon={getFileIcon(file.file_name)} className="h-5 w-5 text-gray-500" />
                              {file.status === 'uploading' && (
                                <Loader className="h-5 w-5 text-blue-500 animate-spin" />
                              )}
                              {file.status === 'completed' && (
                                <CheckCircle className="h-5 w-5 text-black" />
                              )}
                              {file.status === 'error' && (
                                <AlertCircle className="h-5 w-5 text-red-500" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {file.file_name}
                                </p>
                                  {/* ✅ Show error message if status is error and error message exists */}
                                  {file.status === 'error' && file.error && (
                                    <p className="text-sm text-red-500 mt-1">
                                      {file.error}
                                    </p>
                                  )}
                              </div>
                            </div>
                            {file.status === 'uploading' && (
                              <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-4">
                                <div
                                  className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                                  style={{ width: `${file.progress}%` }}
                                ></div>
                              </div>
                            )}
                            <button
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                removeFile(file.sequence);
                              }}
                              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                            >
                              <Trash2 className="h-5 w-5 text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalenderSchedule;
                        